import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../styles/DropdownSystem.css";

export default function StyledSelect({
  value,
  selectedLabel,
  options,
  onChange,
  className = "",
  minWidth = 160,
  ariaLabel = "Select option",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const wrapperRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const target = event.target;
      if (
        !wrapperRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick);
    const handleCloseRequest = () => setOpen(false);
    window.addEventListener("nw:close-dropdowns", handleCloseRequest);
    return () => {
      document.removeEventListener("pointerdown", handleOutsideClick);
      window.removeEventListener("nw:close-dropdowns", handleCloseRequest);
    };
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;

    const updateMenuPosition = () => {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mobile = window.innerWidth <= 768;
      const width = mobile ? Math.min(Math.max(rect.width, 240), window.innerWidth - 16) : rect.width;
      const below = window.innerHeight - rect.bottom - 12;
      const above = rect.top - 12;
      const openAbove = mobile && below < 180 && above > below;
      setMenuPosition({
        top: openAbove ? undefined : rect.bottom + 4,
        bottom: openAbove ? window.innerHeight - rect.top + 4 : undefined,
        left: mobile ? Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)) : rect.left,
        width,
        maxHeight: mobile ? Math.max(0, Math.min(320, openAbove ? above : below)) : 320,
      });
    };

    updateMenuPosition();
    const closeMenu = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };

    window.addEventListener("resize", closeMenu);
    window.addEventListener("scroll", closeMenu, true);

    return () => {
      window.removeEventListener("resize", closeMenu);
      window.removeEventListener("scroll", closeMenu, true);
    };
  }, [open]);

  const selected = options.find((option) => String(option.value) === String(value));
  const menu =
    open && menuPosition
      ? createPortal(
          <div
            ref={menuRef}
            className="nw-styled-select-menu"
            role="listbox"
            style={menuPosition}
          >
            {options.map((option) => {
              const active = String(option.value) === String(value);
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`nw-styled-select-option ${active ? "active" : ""} ${option.className || ""}`}
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>,
          document.body
        )
      : null;

  return (
    <div
      ref={wrapperRef}
      className={`nw-styled-select ${className}`.trim()}
      style={{ minWidth }}
    >
      <button
        type="button"
        className={`nw-styled-select-toggle ${open ? "open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{selected?.label || selectedLabel || "Select"}</span>
        <i className="fa-solid fa-chevron-down" aria-hidden="true" />
      </button>

      {menu}
    </div>
  );
}
